import * as formatMessage from 'format-message';
var msPerDay = 1000 * 60 * 60 * 24;
var msPerMinute = 1000 * 60;
var secPerMinute = 60;
var secPerHour = 60 * 60;
var FriendlyDateFormatter = /** @class */ (function () {
    // An optional param shortFormat is introduced to add support for displaying timestamp in a shorter format as well.
    // This can be leveraged by features that want to display short format as opposed to the default long format.
    // Some examples of short format can be - 1 min ago, 12 hr ago, Fri at 12:56 PM , Jan 29 at 12:30 PM
    function FriendlyDateFormatter(uiCulture, dataCulture, localizedStrings, shortFormat) {
        // As dates are normally formatted with the data culture, it would make some sense to format friendly dates with the data culture as well.
        // It happens that actually pivoting localized files off of data culture is problematic for several reasons.  So we avoid doing it...
        // If the data and UI cultures are the same, the point is moot and we can choose either.  Else, fall back to non-friendly dates formatted
        // by the built-in browser methods.  Server logging has been added to determine how frequently this happens and help us decide
        // if we need a different strategy.
        this.dataCulture = dataCulture;
        this.friendlyDatesEnabled = uiCulture === dataCulture;
        this.localizedStrings = localizedStrings;
        this.shortFormat = shortFormat ? true : false;
    }
    /**
     * Convert the passed date to a friendly string, relative to the current time.
     * @param inputDate date to be converted to a string.
     * @param testNow optional date to represent 'now' for unit testing.  production use should not include this parameter.
     */
    FriendlyDateFormatter.prototype.format = function (inputDate, testNow) {
        // fall back to non-friendly dates localized by the browser
        if (!this.friendlyDatesEnabled) {
            return this.fallbackFormat(inputDate);
        }
        // compute local day numbers
        var now = testNow || new Date();
        var secondsAgo = (now.getTime() - inputDate.getTime()) / 1000;
        var todayDay = Math.floor((now.getTime() - now.getTimezoneOffset() * msPerMinute) / msPerDay);
        var inputDay = Math.floor((inputDate.getTime() - inputDate.getTimezoneOffset() * msPerMinute) / msPerDay);
        // past
        if (inputDate <= now) {
            if (secondsAgo < 2 * secPerMinute) {
                // less than 2 minutes ago
                return this.localizedStrings.JustNow;
            }
            if (secondsAgo < 1 * secPerHour) {
                // less than 1 hour ago
                var mins = Math.floor(secondsAgo / 60);
                if (this.shortFormat) {
                    return this.localizedStrings.MinAgo.replace('{0}', mins.toString());
                }
                return this.getMessageFormatter()(this.localizedStrings.MinutesAgo, { mins: mins });
            }
            if (inputDay === todayDay) {
                // today
                var hours = Math.floor(secondsAgo / secPerHour);
                if (this.shortFormat) {
                    return this.localizedStrings.HrAgo.replace('{0}', hours.toString());
                }
                return this.getMessageFormatter()(this.localizedStrings.HoursAgo, { hours: hours });
            }
            if (inputDay === todayDay - 1) {
                // yesterday
                return this.localizedStrings.YesterdayAt.replace('{0}', this.getTimeString(inputDate));
            }
            if (inputDay >= todayDay - 6 && inputDay < todayDay) {
                // last week
                return this.localizedStrings.DateTimeCombo.replace('{0}', Intl.DateTimeFormat(this.dataCulture, { weekday: this.shortFormat ? 'short' : 'long' }).format(inputDate)).replace('{1}', this.getTimeString(inputDate));
            }
            if (inputDate.getFullYear() === now.getFullYear() && inputDay < todayDay) {
                // same calendar year
                return this.localizedStrings.DateTimeCombo.replace('{0}', Intl.DateTimeFormat(this.dataCulture, { month: this.shortFormat ? 'short' : 'long', day: 'numeric' }).format(inputDate)).replace('{1}', this.getTimeString(inputDate));
            }
        }
        // far past, or [error case] future.
        return Intl.DateTimeFormat(this.dataCulture, {
            month: this.shortFormat ? 'short' : 'long',
            day: 'numeric',
            year: 'numeric',
        }).format(inputDate);
    };
    FriendlyDateFormatter.prototype.getTimeString = function (inputDate) {
        return Intl.DateTimeFormat(this.dataCulture, { hour: 'numeric', minute: 'numeric' }).format(inputDate);
    };
    /**
     * When the data culture and language culture do not align, we punt and fall back to the non-friendly dates formatted by the browser.
     */
    FriendlyDateFormatter.prototype.fallbackFormat = function (inputDate) {
        return Intl.DateTimeFormat(this.dataCulture, {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
        }).format(inputDate);
    };
    FriendlyDateFormatter.prototype.getMessageFormatter = function () {
        if (!this._messageFormatter) {
            this._messageFormatter = formatMessage.namespace();
            this._messageFormatter.setup({ locale: this.dataCulture, missingTranslation: 'ignore' });
        }
        return this._messageFormatter;
    };
    return FriendlyDateFormatter;
}());
export { FriendlyDateFormatter };
//# sourceMappingURL=FriendlyDateFormatter.js.map